import PasswordValidation from "@app/components/Account/PasswordValidation/PasswordValidation.jsx"
import { ContentBody } from "@app/components/core/ContentBody/ContentBody"
import { ContentHeader } from "@app/components/core/ContentHeader/ContentHeader"
import Button from "@app/components/move2Library/Button/Button"
import { Checkbox } from "@app/components/move2Library/Checkbox/Checkbox"
import Input from "@app/components/move2Library/Input/Input"
import { SelectBox } from "@app/components/move2Library/SelectBox/SelectBox"
import { useCustomNavigation } from "@app/components/navigation/NavigationHandler"
import ImagePage from "@app/components/pageTypes/ImagePage/ImagePage"
import AnimatedRoute, { AnimationTypes } from "@app/components/routing/AnimatedRoute/AnimatedRoute"
import { useGetLinks, useGetStrings } from "@app/store/service/cmsService"
import { useGetCountries, useGetSalutations, useRegister, useStartVerificationByMail } from "@app/store/service/connectorService"
import { LinkTypes } from "@app/utils/constants"
import { RenderHTML } from "@app/utils/htmlUtils"
import { emailPattern } from "@app/utils/validations"
import { useViewport } from "@tecreation/funnel-components"
import { useCallback, useState } from "react"
import { useForm } from "react-hook-form"

import RadioButtonsGroup from "@app/components/move2Library/RadioButtonsGroup/RadioButtonsGroup"
import { useRaiseError } from "@app/store/actions/errorActions"
import { appStore } from "@app/store/store"
import "./CreateAccountPage.css"

const getLinkUrl = (linkData = {}) => {
	if (appStore.get("core.kioskMode")) return linkData.kioskBaseUrl
	return linkData.baseUrl
}

const CreateAccountPage = () => {
	const [showCompany, setShowCompany] = useState(false)
	const [showPasswordValidation, setShowPasswordValidation] = useState(false)

	const { replace, push } = useCustomNavigation()
	const { sm, md } = useViewport()
	const raiseError = useRaiseError()

	const [startVerificationbyMail, { isLoading: verificationLoading }] = useStartVerificationByMail()
	const [registerAccount] = useRegister()
	const { data: countries } = useGetCountries()
	const { data: salutations, isLoading } = useGetSalutations()
	const { data: linkData } = useGetLinks()
	const {
		data: {
			createAccountPageTitle,
			buttonRegister,
			clientTypes,
			placeholderFirstName,
			placeholderLastName,
			placeholderEMail,
			placeholderEMailRepeat,
			placeholderPassword,
			placeholderPasswordRepeat,
			labelSalutation,
			createAccountPageAccountTypeText,
			placeholderCountry,
			placeholderCompany,
			placeholderVATNumber,
			requiredFields,
			privacyConsent,
			pleaseSelect,

			errorInputRequiredField,
			errorInputMissingEmail,
			errorInputMissingPassword,
			errorInputMissingFirstName,
			errorInputMissingLastName,
			errorInputInvalidEmail,
			errorInputMissingSalutation,
			errorInputMissingEmailRepeat,
			errorInputMissingPasswordRepeat,
			errorInputMissingCompany,
			errorInputMissingVATNumber,
			errorInputMissingCountry,
			errorInputMustBeTheSameEmailRepeat,
			errorInputMustBeTheSamePasswordRepeat,
			errorInputPwRequirement,
		},
	} = useGetStrings(undefined, { defaultData: {} })

	const {
		register,
		handleSubmit,
		formState: { errors },
		getValues,
		setValue,
		trigger,
		watch,
	} = useForm({
		mode: "onTouched",
		defaultValues: {
			salutation: "",
			firstName: "",
			lastName: "",
			email: "",
			emailRepeat: "",
			phoneNo: "",
			street: "",
			zipCode: "",
			city: "",
			countryId: "",
			accountType: "privat",
			password: "",
			passwordRepeat: "",
			companyName: "",
			vatNumber: "",
			birthday: "",
			newsletterSignup: false,
			privacyConsent: false,
		},
	})

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const onRegisterClick = useCallback(async () => {
		handleSubmit(async data => {
			data.genderId = data.salutation
			try {
				let result = await registerAccount(data).unwrap()
				if (result.errorMessage) {
					throw result
				}
				let emailResult = await startVerificationbyMail({ relationToken: result.relationToken, email: result.email }).unwrap()
				emailResult && replace("/login?action=createdAccount", AnimationTypes.stack, {}, "POP")
			} catch (e) {
				if (e.errorMessage === "wrn.Relation.Unverified") {
					push("/account-not-verified", undefined, { email: data.email, relationToken: e.relationToken })
				} else {
					raiseError(e)
				}
			}
		})()
	})

	const privacyLink = linkData?.links?.find(e => e.type === LinkTypes.privacy)
	const privacyUrl = privacyLink ? getLinkUrl(linkData) + appStore.get("core.languageCode") + "/" + privacyLink.slug : ""
	return (
		<AnimatedRoute className="h-100">
			<ImagePage>
				<ContentHeader canGoBack colorStyle="white" animation={AnimationTypes.slide}>
					{createAccountPageTitle}
				</ContentHeader>
				<ContentBody className="flex flex-column container-lg f4 flex-1" scrollable>
					<form aria-label={createAccountPageTitle}>
						<div className="createAccountPage__inputRow createAccountPage__inputRow--3">
							<RadioButtonsGroup
								label={labelSalutation}
								options={salutations}
								errors={errors}
								formRegister={register("salutation", {
									required: {
										value: true,
										message: errorInputMissingSalutation,
									},
								})}
							/>

							<RadioButtonsGroup
								label={createAccountPageAccountTypeText}
								options={clientTypes}
								errors={errors}
								formRegister={register("accountType", {
									required: {
										value: true,
										message: "missing",
									},
								})}
								onChange={e => {
									setShowCompany(e.target.value === "firma")
								}}
							/>
						</div>

						{showCompany && (
							<div className="createAccountPage__inputRow createAccountPage__inputRow--3">
								<div>
									<Input
										type="text"
										label={placeholderCompany + "*"}
										formRegister={register("companyName", {
											required: {
												value: true,
												message: errorInputMissingCompany,
											},
										})}
										errors={errors}
									/>
								</div>
								<div>
									<Input
										type="text"
										label={placeholderVATNumber + "*"}
										formRegister={register("vatNumber", {
											required: {
												value: true,
												message: errorInputMissingVATNumber,
											},
										})}
										errors={errors}
									/>
								</div>
							</div>
						)}

						<div className="createAccountPage__inputRow createAccountPage__inputRow--3">
							<div>
								<Input
									name="firstname"
									type="text"
									label={placeholderFirstName + "*"}
									autocomplete="given-name"
									formRegister={register("firstName", {
										required: {
											value: true,
											message: errorInputMissingFirstName,
										},
									})}
									errors={errors}
								/>
							</div>
							<div>
								<Input
									name="lastname"
									type="text"
									label={placeholderLastName + "*"}
									autocomplete="family-name"
									formRegister={register("lastName", {
										required: {
											value: true,
											message: errorInputMissingLastName,
										},
									})}
									errors={errors}
								/>
							</div>
						</div>

						<div className="createAccountPage__inputRow createAccountPage__inputRow--3">
							<SelectBox
								classname="selectbox border rounded-1 fill"
								autocomplete="country"
								formRegister={register("countryId", {
									required: {
										value: true,
										message: errorInputMissingCountry,
									},
								})}
								onChange={e => {
									setValue("countryId", e.target.value)
								}}
								errors={errors}
								label={placeholderCountry + "*"}
							>
								<option value="" disabled style={{ display: "none" }}>
									{pleaseSelect}
								</option>
								{countries &&
									countries.map(country => {
										return (
											<option key={country.countryId} value={country.countryId}>
												{country.country}
											</option>
										)
									})}
							</SelectBox>
						</div>

						{/* <div className="createAccountPage__inputRow createAccountPage__inputRow--1_2">
                            <Input
                                name={"postalCode"}
                                type="text"
                                label={placeholderPostalCode + "*"}
                                autocomplete={"postal-code"}
                                formRegister={register("zipCode", {
                                    required: {
                                        value: true,
                                        message: errorInputMissingPostalCode,
                                    },
                                })}
                                errors={errors}
                            />
                            <Input
                                name={"city"}
                                type="text"
                                label={placeholderCity + "*"}
                                autocomplete={"address-level2"}
                                formRegister={register("city", {
                                    required: {
                                        value: true,
                                        message: errorInputMissingCity,
                                    },
                                })}
                                errors={errors}
                            />
                        </div> */}

						{/* 
                        <div className="createAccountPage__inputRow createAccountPage__inputRow--3">
                            <Input
                                name={"phoneNo"}
                                type="tel"
                                label={placeholderPhone}
                                autocomplete={"on"}
                                formRegister={register("phoneNo", {})}
                                errors={errors}
                            />


                        </div> */}

						<div className="createAccountPage__inputRow createAccountPage__inputRow--3 mt-8">
							<div>
								<Input
									name="email"
									type="email"
									label={placeholderEMail + "*"}
									autocomplete="email"
									formRegister={register("email", {
										required: {
											value: true,
											message: errorInputMissingEmail,
										},
										pattern: {
											value: emailPattern,
											message: errorInputInvalidEmail,
										},
										validate: () => {
											if (getValues("emailRepeat").length) {
												trigger("emailRepeat")
											}
											return true
										},
									})}
									errors={errors}
								/>
							</div>
							<div>
								<Input
									name="emailRepeat"
									type="email"
									label={placeholderEMailRepeat + "*"}
									formRegister={register("emailRepeat", {
										required: {
											value: true,
											message: errorInputMissingEmailRepeat,
										},
										validate: {
											equalsEmail: value => value === getValues().email || errorInputMustBeTheSameEmailRepeat,
										},
									})}
									errors={errors}
								/>
							</div>
						</div>

						<div className="createAccountPage__inputRow createAccountPage__inputRow--3">
							<div>
								<Input
									type="password"
									label={placeholderPassword + "*"}
									autocomplete="off"
									onFocusChange={isFocused => {
										setShowPasswordValidation(isFocused)
									}}
									formRegister={register("password", {
										required: {
											value: true,
											message: errorInputMissingPassword,
										},
										validate: () => {
											if (getValues("passwordRepeat").length) {
												trigger("passwordRepeat")
											}
											return true
										},
										pattern: {
											value: /(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])(?=.{8,})/,
											message: errorInputPwRequirement,
										},
									})}
								/>
								{showPasswordValidation && (
									<div className="createAccountPage__passwordValidationContainer">
										<PasswordValidation password={watch("password") || ""} />
									</div>
								)}
							</div>
							<div>
								<Input
									type="password"
									label={placeholderPasswordRepeat + "*"}
									autocomplete="off"
									formRegister={register("passwordRepeat", {
										required: {
											value: true,
											message: errorInputMissingPasswordRepeat,
										},
										validate: {
											equalsPassword: value => value === getValues().password || errorInputMustBeTheSamePasswordRepeat,
										},
									})}
									errors={errors}
								/>
							</div>
						</div>

						{/* Enable this once the backend logic has been decided */}
						{/* <div className="mt-2 f5" style={{ fontStyle: "italic" }}>
							<Checkbox
								label={newsletterSignup}
								formRegister={register("newsletterSignup")}
								className="mt-3"
								checked={false}
							/>
						</div> */}

						<div className="mt-2 mb-4 f5" style={{ fontStyle: "italic" }}>
							<Checkbox
								label={<RenderHTML html={privacyConsent.replace("${link}", privacyUrl)} />}
								formRegister={register("privacyConsent", {
									required: {
										value: true,
										message: errorInputRequiredField,
									},
								})}
								className="mt-3"
								errors={errors}
							/>
						</div>

						<div className="mt-2 f5" style={{ fontStyle: "italic" }}>
							* {requiredFields}
						</div>

						<div className={"mt-4" + (sm || md ? " flex" : "")}>
							<Button style="primary" onClick={onRegisterClick} isLoading={isLoading || verificationLoading}>
								{buttonRegister}
							</Button>
						</div>
					</form>
				</ContentBody>
			</ImagePage>
		</AnimatedRoute>
	)
}

export default CreateAccountPage
